import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import { fiterFilters, objectToString } from "./parser";

/* -------------------------------------------------
   SORT DATA BASED ON SORT RULES
-------------------------------------------------- */
function sortDataByRules(data, sortRules = []) {
  if (!sortRules || sortRules.length === 0 || !data) {
    return data;
  }

  return [...data].sort((a, b) => {
    for (const rule of sortRules) {
      const { id, desc } = rule;

      // Get values for comparison
      const aValue = a[id];
      const bValue = b[id];

      // Handle null/undefined values
      if (aValue == null && bValue == null) continue;
      if (aValue == null) return desc ? 1 : -1;
      if (bValue == null) return desc ? -1 : 1;

      // Determine comparison result
      let comparison = 0;

      // Handle different data types
      if (typeof aValue === "string" && typeof bValue === "string") {
        // Case-insensitive string comparison
        comparison = aValue.localeCompare(bValue, undefined, {
          sensitivity: "base",
          numeric: true,
        });
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else {
        // Fallback to string comparison
        comparison = String(aValue).localeCompare(String(bValue), undefined, {
          sensitivity: "base",
          numeric: true,
        });
      }

      // If values are different, return comparison result
      if (comparison !== 0) {
        return desc ? comparison * -1 : comparison;
      }
    }

    // If all sort rules result in equality, preserve original order
    return 0;
  });
}

/* -------------------------------------------------
   HELPER FUNCTIONS FOR PDF
-------------------------------------------------- */

// Unified column width calculation for both headers and data
function calculateColumnWidths(headers, data, columnKeys, pageWidth, margin) {
  const availableWidth = pageWidth - margin * 2;
  const minWidth = 30;
  const maxWidth = availableWidth / (headers.length || 1);

  if (headers.length === 0) return [availableWidth];

  // Define column priority (1 = highest priority, 3 = lowest)
  const columnPriority = {
    email: 1,
    transaction: 1,
    id: 2,
    amount: 2,
    total: 2,
    donor: 2,
    status: 3,
    type: 3,
    card: 3,
    created: 3,
    tip: 3,
    actual_fee: 3,
    refund_available: 3,
  };

  // Calculate widths for each column
  const columnWidths = headers.map((header, colIndex) => {
    const headerLength = header.length;
    const key = columnKeys[colIndex]?.toLowerCase() || "";

    // Find max content length in this column from data
    let maxContentLength = 0;
    data.forEach((row) => {
      const value = row[columnKeys[colIndex]];
      if (value !== null && value !== undefined) {
        const strValue = String(value);
        // For money fields, we'll format them, so estimate the length
        if (
          key.includes("amount") ||
          key.includes("fee") ||
          key.includes("total") ||
          key.includes("tip")
        ) {
          maxContentLength = Math.max(maxContentLength, 10); // Approx length for "$1,234.56"
        } else {
          maxContentLength = Math.max(maxContentLength, strValue.length);
        }
      }
    });

    // Use the maximum of header length or max content length
    const maxLength = Math.max(headerLength, maxContentLength);

    // Base width calculation
    let baseWidth = Math.max(maxLength * 6, minWidth);

    // Apply priority multiplier
    const priority = columnPriority[key] || 3;
    const priorityMultiplier =
      priority === 1 ? 1.4 : priority === 2 ? 1.2 : 1.0;

    const calculatedWidth = Math.min(baseWidth * priorityMultiplier, maxWidth);

    return Math.max(calculatedWidth, minWidth);
  });

  // Adjust widths to fit available space
  const totalWidth = columnWidths.reduce((sum, width) => sum + width, 0);

  if (totalWidth > availableWidth) {
    const scaleFactor = availableWidth / totalWidth;
    return columnWidths.map((width) => Math.max(width * scaleFactor, minWidth));
  }

  // If we have extra space, distribute it proportionally
  const extraWidth = availableWidth - totalWidth;
  if (extraWidth > 0) {
    const totalPriorityWeight = columnWidths.reduce((sum, width, idx) => {
      const key = columnKeys[idx]?.toLowerCase() || "";
      const priority = columnPriority[key] || 3;
      return sum + (priority === 1 ? 3 : priority === 2 ? 2 : 1);
    }, 0);

    return columnWidths.map((width, idx) => {
      const key = columnKeys[idx]?.toLowerCase() || "";
      const priority = columnPriority[key] || 3;
      const priorityWeight = priority === 1 ? 3 : priority === 2 ? 2 : 1;
      const extraAllocation =
        (priorityWeight / totalPriorityWeight) * extraWidth;
      return width + extraAllocation;
    });
  }

  return columnWidths;
}

// Format cell value - convert cents to dollars for money fields
function formatCellValue(value, columnKey, moneyFields = [], maxLength = null) {
  if (value === null || value === undefined) return "";

  // Check if this is a money field
  if (moneyFields.includes(columnKey)) {
    // Convert cents to dollars with 2 decimal places
    const amountInDollars = Number(value) / 100;
    return `$${amountInDollars.toFixed(2)}`;
  }

  // Format dates
  if (
    value instanceof Date ||
    (typeof value === "string" && !isNaN(Date.parse(value)))
  ) {
    const date = new Date(value);
    return date.toLocaleDateString();
  }

  // Handle truncation
  let strValue = String(value);
  if (maxLength && strValue.length > maxLength) {
    return strValue.substring(0, maxLength - 3) + "...";
  }

  return strValue;
}

// Helper function to format filter keys for display
function formatFilterKey(key) {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

// List of money fields (can be extended)
const DEFAULT_MONEY_FIELDS = [
  "amount",
  "donation_amount",
  "platform_tip_amount",
  "original_fee",
  "actual_fee",
  "actual_charge_amount",
  "refund_available",
  "profit_loss",
  "net_amount",
  "total_amount",
  "total_fees",
  "minAmount",
  "maxAmount",
  "donation_amount_min",
  "donation_amount_max",
  "actual_charge_amount_min",
  "actual_charge_amount_max",
  "profit_loss_min",
  "profit_loss_max",
  "net_amount_min",
  "net_amount_max",
  "total_amount_min",
  "total_amount_max",
  "total_fees_min",
  "total_fees_max",
];

/* -------------------------------------------------
   FUNCTION TO LOAD AND EMBED LOGO IMAGE
-------------------------------------------------- */
async function loadAndEmbedLogo(pdfDoc, logoUrl) {
  try {
    console.log("Attempting to load logo from:", logoUrl);

    // For browser environment, we need to fetch the image
    let imageBytes;

    if (logoUrl.startsWith("http") || logoUrl.startsWith("//")) {
      // Absolute URL
      const response = await fetch(logoUrl);
      if (!response.ok) {
        console.warn("Could not fetch logo from absolute URL:", logoUrl);
        return null;
      }
      imageBytes = await response.arrayBuffer();
    } else {
      // Relative path - construct full URL
      const baseUrl = window.location.origin;
      const fullUrl = logoUrl.startsWith("/")
        ? `${baseUrl}${logoUrl}`
        : `${baseUrl}/${logoUrl}`;

      console.log("Fetching logo from full URL:", fullUrl);
      const response = await fetch(fullUrl);

      if (!response.ok) {
        console.warn("Could not fetch logo from relative path:", fullUrl);
        return null;
      }
      imageBytes = await response.arrayBuffer();
    }

    // Try to embed as PNG
    try {
      const image = await pdfDoc.embedPng(imageBytes);
      console.log("Logo embedded successfully as PNG");
      return image;
    } catch (pngError) {
      console.log("Not a PNG, trying JPG...");
      try {
        const image = await pdfDoc.embedJpg(imageBytes);
        console.log("Logo embedded successfully as JPG");
        return image;
      } catch (jpgError) {
        console.warn("Could not embed logo as PNG or JPG:", jpgError);
        return null;
      }
    }
  } catch (error) {
    console.warn("Error loading logo:", error);
    return null;
  }
}

/* -------------------------------------------------
   PDF EXPORT FUNCTION - FIXED COLUMN WIDTH MATCHING
-------------------------------------------------- */
async function performPDFExport({
  data,
  columns,
  exportFileName,
  filtersValues,
  discardedFields = [],
  sortRules = [],
  shouldSort = true,
  companyInfo = {
    name: "Purly Give",
    logoUrl: "/logoWithText.png",
    address: "",
    phone: "",
    email: "",
    website: "",
  },
  reportTitle = "",
  moneyFields = DEFAULT_MONEY_FIELDS,
  onSuccess,
}) {
  // Sort data based on sort rules before processing
  const sortedData = shouldSort ? sortDataByRules(data, sortRules) : data;

  // Extract column information
  const columnTitles = (columns || [])
    .map((c) => c.Header || c.header || c.title || c.id || c.accessorKey)
    .filter(Boolean);

  const columnKeys = (columns || [])
    .map((c) =>
      typeof c.accessor === "string"
        ? c.accessor
        : typeof c.dataIndex === "string"
        ? c.dataIndex
        : c.id
    )
    .filter(Boolean);

  // Remove discarded fields
  const filteredKeys = columnKeys.filter((k) => {
    return !discardedFields.includes(k);
  });
  const filteredHeaders =
    columnTitles.length > 0
      ? columnTitles.filter((_, idx) => filteredKeys.includes(columnKeys[idx]))
      : filteredKeys;

  // Create PDF document
  const pdfDoc = await PDFDocument.create();

  // Set page size to A4 Landscape for more horizontal space
  const pageWidth = 841.89; // A4 landscape width
  const pageHeight = 595.28; // A4 landscape height
  const margin = 30;

  let page = pdfDoc.addPage([pageWidth, pageHeight]);

  // Embed fonts
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  // Set initial coordinates
  let x = margin;
  let y = pageHeight - margin;
  const lineHeight = 14;
  const dataLineHeight = 12;

  /* -------------------------------------------------
     HEADER SECTION - Company Info with Logo
  -------------------------------------------------- */

  let logo = null;
  let logoWidth = 0;

  if (companyInfo.logoUrl) {
    logo = await loadAndEmbedLogo(pdfDoc, companyInfo.logoUrl);
  }

  if (logo) {
    // Draw logo with reasonable size
    logoWidth = 150;
    const logoAspectRatio = logo.width / logo.height;
    const finalHeight = logoWidth / logoAspectRatio;
    console.log({ finalHeight });

    page.drawImage(logo, {
      x: x,
      y: y - finalHeight,
      width: logoWidth,
      height: finalHeight,
    });

    // Draw company name next to logo
    // page.drawText(companyInfo.name, {
    //   x: x + logoWidth + 10,
    //   y: y - 15,
    //   font: boldFont,
    //   size: 16,
    //   color: rgb(0, 0, 0),
    // });

    y -= finalHeight + 15;
  } else {
    // Draw company name without logo
    console.warn("Logo not loaded, displaying text only");
    page.drawText(companyInfo.name, {
      x: x,
      y: y,
      font: boldFont,
      size: 16,
      color: rgb(0, 0, 0),
    });
    y -= 25;
  }

  /* -------------------------------------------------
     REPORT TITLE SECTION
  -------------------------------------------------- */

  // Draw report title
  const reportTitleText = reportTitle || `${exportFileName}`;
  // const reportTitleText = reportTitle || `${exportFileName} Report`;
  const titleWidth = reportTitleText.length * 7;
  page.drawText(reportTitleText, {
    x: (pageWidth - titleWidth) / 2,
    y: y,
    font: boldFont,
    size: 14,
    color: rgb(0, 0.2, 0.4),
  });

  y -= 25;

  /* -------------------------------------------------
     REPORT METADATA SECTION
  -------------------------------------------------- */

  // Draw generation date and total records
  const generationDate = new Date().toLocaleDateString();
  const generationTime = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  const metadataText = `Generated: ${generationDate} ${generationTime} | Records: ${sortedData.length}`;
  page.drawText(metadataText, {
    x: margin,
    y: y,
    font: font,
    size: 9,
    color: rgb(0.5, 0.5, 0.5),
  });

  y -= 15;

  /* -------------------------------------------------
     FILTER & SORT INFO SECTION (Compact)
  -------------------------------------------------- */

  // Get filters excluding pagination fields
  const filters = fiterFilters(filtersValues, ["page", "itemsPerPage"]);
  const hasFilters = Object.keys(filters).length > 0;

  let infoLines = [];

  if (hasFilters) {
    const filterText = Object.entries(filters)
      .filter(([key, value]) => value && value !== "")
      .map(([key, value]) => `${formatFilterKey(key)}: ${value}`)
      .join(" | ");

    if (filterText) {
      infoLines.push(`Filters: ${filterText}`);
    }
  }

  if (sortRules.length > 0) {
    const sortText = sortRules
      .map((rule) => `${rule.id} (${rule.desc ? "desc" : "asc"})`)
      .join(", ");
    infoLines.push(`Sorted by: ${sortText}`);
  }

  // Draw info lines
  for (const line of infoLines) {
    if (y < margin + 100) break;

    page.drawText(line, {
      x: margin,
      y: y,
      font: font,
      size: 8,
      color: rgb(0.3, 0.3, 0.3),
      maxWidth: pageWidth - margin * 2,
    });

    y -= 12;
  }

  y -= 10;

  // Add separator line
  page.drawLine({
    start: { x: margin, y: y },
    end: { x: pageWidth - margin, y: y },
    thickness: 0.5,
    color: rgb(0.8, 0.8, 0.8),
  });

  y -= 15;

  /* -------------------------------------------------
     TABLE SECTION - FIXED COLUMN POSITIONS
  -------------------------------------------------- */

  // Check if we need a new page before starting the table
  if (y < margin + 80) {
    page = pdfDoc.addPage([pageWidth, pageHeight]);
    y = pageHeight - margin;
  }

  // Calculate column widths for both headers and data
  const headersWithRowNum = ["#", ...filteredHeaders];
  const keysWithRowNum = ["rowNum", ...filteredKeys];

  const columnWidths = calculateColumnWidths(
    headersWithRowNum,
    sortedData,
    keysWithRowNum,
    pageWidth,
    margin
  );

  // Store column positions for consistent alignment
  const columnPositions = [];
  const totalTableWidth = columnWidths.reduce((sum, width) => sum + width, 0);
  const tableStartX = Math.max(margin, (pageWidth - totalTableWidth) / 2);

  // Calculate exact X positions for each column
  let currentX = tableStartX;
  columnWidths.forEach((width, index) => {
    columnPositions.push(currentX);
    currentX += width;
  });

  const headerBackgroundColor = rgb(0.9, 0.9, 0.95);
  const headerTextColor = rgb(0, 0, 0.4);

  /* -------------------------------------------------
     DRAW HEADERS WITH FIXED POSITIONS
  -------------------------------------------------- */

  // Draw header background
  page.drawRectangle({
    x: columnPositions[0] - 3,
    y: y + 3,
    width: totalTableWidth + 6,
    height: lineHeight + 6,
    color: headerBackgroundColor,
  });

  // Draw header text using fixed positions
  headersWithRowNum.forEach((header, colIndex) => {
    const columnX = columnPositions[colIndex];
    const columnWidth = columnWidths[colIndex];
    const key = keysWithRowNum[colIndex]?.toLowerCase() || "";

    // Determine font size based on column type
    let fontSize = 9;
    if (["email", "transaction"].includes(key)) {
      fontSize = 8;
    } else if (
      [
        "amount",
        "total",
        "id",
        "tip",
        "actual_fee",
        "refund_available",
      ].includes(key)
    ) {
      fontSize = 9;
    }

    // Truncate header if too long for column
    let displayHeader = header;
    const maxCharsInColumn = Math.floor(columnWidth / (fontSize * 0.6));

    if (header.length > maxCharsInColumn) {
      displayHeader = header.substring(0, maxCharsInColumn - 3) + "...";
    }

    // Center the header text in its column
    const textWidth = displayHeader.length * fontSize * 0.6;
    const textX = columnX + (columnWidth - textWidth) / 2;

    page.drawText(displayHeader, {
      x: Math.max(textX, columnX + 2),
      y: y + 5,
      font: boldFont,
      size: fontSize,
      color: headerTextColor,
    });
  });

  y -= lineHeight + 8;

  /* -------------------------------------------------
     DRAW DATA ROWS USING THE SAME FIXED POSITIONS
  -------------------------------------------------- */

  // Function to draw a data row using the fixed positions
  const drawDataRow = (rowData, rowNumber) => {
    // Check if we need a new page
    if (y < margin + 40) {
      page = pdfDoc.addPage([pageWidth, pageHeight]);
      y = pageHeight - margin;

      // Draw headers on new page using fixed positions
      page.drawRectangle({
        x: columnPositions[0] - 3,
        y: y + 3,
        width: totalTableWidth + 6,
        height: lineHeight + 6,
        color: headerBackgroundColor,
      });

      // Re-draw headers
      headersWithRowNum.forEach((header, colIndex) => {
        const columnX = columnPositions[colIndex];
        const columnWidth = columnWidths[colIndex];
        const key = keysWithRowNum[colIndex]?.toLowerCase() || "";

        let fontSize = 9;
        if (["email", "transaction"].includes(key)) {
          fontSize = 8;
        } else if (
          [
            "amount",
            "total",
            "id",
            "tip",
            "actual_fee",
            "refund_available",
          ].includes(key)
        ) {
          fontSize = 9;
        }

        let displayHeader = header;
        const maxCharsInColumn = Math.floor(columnWidth / (fontSize * 0.6));

        if (header.length > maxCharsInColumn) {
          displayHeader = header.substring(0, maxCharsInColumn - 3) + "...";
        }

        const textWidth = displayHeader.length * fontSize * 0.6;
        const textX = columnX + (columnWidth - textWidth) / 2;

        page.drawText(displayHeader, {
          x: Math.max(textX, columnX + 2),
          y: y + 5,
          font: boldFont,
          size: fontSize,
          color: headerTextColor,
        });
      });

      y -= lineHeight + 8;
    }

    // Alternate row background for readability
    if (rowNumber % 2 === 1) {
      page.drawRectangle({
        x: columnPositions[0] - 2,
        y: y,
        width: totalTableWidth + 4,
        height: dataLineHeight + 2,
        color: rgb(0.98, 0.98, 0.98),
      });
    }

    // Draw ALL columns (including row number) using fixed positions
    headersWithRowNum.forEach((header, colIndex) => {
      const columnX = columnPositions[colIndex];
      const columnWidth = columnWidths[colIndex];

      // Get the cell value
      let displayText;
      if (colIndex === 0) {
        // Row number column
        displayText = rowNumber.toString();
      } else {
        // Data column
        const dataKey = filteredKeys[colIndex - 1];
        const cellValue = rowData[dataKey];

        // Determine max length for this column based on its width
        const maxChars = Math.floor(columnWidth / 5);
        displayText = formatCellValue(
          cellValue,
          dataKey,
          moneyFields,
          maxChars
        );
      }

      // Determine font size and alignment based on column type
      let fontSize = 8;
      let isCentered = true;

      // Row number column
      if (colIndex === 0) {
        fontSize = 9;
        isCentered = true;
      }
      // Data columns
      else {
        const dataKey = filteredKeys[colIndex - 1];
        const keyLower = dataKey?.toLowerCase() || "";

        if (["email", "transaction"].includes(keyLower)) {
          fontSize = 7;
          isCentered = false;
        } else if (
          moneyFields.includes(dataKey) ||
          ["amount", "total", "tip", "actual_fee", "refund_available"].includes(
            keyLower
          )
        ) {
          fontSize = 8;
          isCentered = true;
        } else if (
          ["status", "type", "card", "created", "id"].includes(keyLower)
        ) {
          fontSize = 8;
          isCentered = true;
        }
      }

      // Calculate text position
      let textX = columnX + 3;
      if (isCentered) {
        const estimatedTextWidth = displayText.length * fontSize * 0.6;
        textX = columnX + (columnWidth - estimatedTextWidth) / 2;
      }

      // Ensure text doesn't overflow column
      const maxTextWidth = columnWidth - 6;
      const estimatedTextWidth = displayText.length * fontSize * 0.6;

      if (estimatedTextWidth > maxTextWidth) {
        // Truncate further if needed
        const charsThatFit = Math.floor(maxTextWidth / (fontSize * 0.6));
        if (charsThatFit > 3) {
          displayText = displayText.substring(0, charsThatFit - 3) + "...";
        }
      }

      page.drawText(displayText, {
        x: textX,
        y: y + 2,
        font: font,
        size: fontSize,
        color: rgb(0, 0, 0),
      });
    });

    y -= dataLineHeight + 2;
  };

  // Draw all data rows
  sortedData.forEach((row, index) => {
    drawDataRow(row, index + 1);
  });

  /* -------------------------------------------------
     FOOTER SECTION
  -------------------------------------------------- */

  // Add page number on each page
  const pages = pdfDoc.getPages();
  pages.forEach((page, index) => {
    const pageNum = index + 1;
    const totalPages = pages.length;

    page.drawText(`Page ${pageNum} of ${totalPages}`, {
      x: pageWidth / 2 - 25,
      y: 25,
      font: font,
      size: 8,
      color: rgb(0.5, 0.5, 0.5),
    });

    // Add footer separator line
    page.drawLine({
      start: { x: margin, y: 30 },
      end: { x: pageWidth - margin, y: 30 },
      thickness: 0.3,
      color: rgb(0.8, 0.8, 0.8),
    });
  });

  /* -------------------------------------------------
     SAVE AND DOWNLOAD PDF
  -------------------------------------------------- */

  const pdfBytes = await pdfDoc.save();
  const blob = new Blob([pdfBytes], { type: "application/pdf" });

  // Create filename with date
  const fileName = `${exportFileName}_Report_${new Date()
    .toISOString()
    .slice(0, 10)}.pdf`;
  saveAs(blob, fileName);

  onSuccess?.();
}

/* -------------------------------------------------
   MAIN EXPORT FUNCTION
-------------------------------------------------- */
async function performFileExport({
  format,
  data,
  columns,
  exportFileName,
  filtersValues,
  discardedFields = [],
  onSuccess,
  sortRules = [],
  shouldSort = true,
  companyInfo = {
    name: "Purly Give",
    logoUrl: "/logoWithText.png",
    address: "",
    phone: "",
    email: "",
    website: "",
  },
  reportTitle = "",
  moneyFields = DEFAULT_MONEY_FIELDS,
}) {
  console.log({ sortRules, shouldSort });

  // Sort data based on sort rules before processing
  const sortedData = shouldSort ? sortDataByRules(data, sortRules) : data;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Sheet1");

  // Extract column titles & keys
  const columnTitles = (columns || [])
    .map((c) => c.Header || c.header || c.title || c.id || c.accessorKey)
    .filter(Boolean);

  const columnKeys = (columns || [])
    .map((c) =>
      typeof c.accessor === "string"
        ? c.accessor
        : typeof c.dataIndex === "string"
        ? c.dataIndex
        : c.id
    )
    .filter(Boolean);

  // Remove discarded fields
  const filteredKeys = columnKeys.filter((k) => {
    return !discardedFields.includes(k);
  });
  const filteredHeaders =
    columnTitles.length > 0
      ? columnTitles.filter((_, idx) => filteredKeys.includes(columnKeys[idx]))
      : filteredKeys;

  // Excel worksheet columns with optimized widths
  worksheet.columns = filteredKeys.map((key, idx) => {
    const keyLower = key.toLowerCase();
    let width = 15;

    if (keyLower.includes("email")) width = 40;
    else if (keyLower.includes("transaction")) width = 25;
    else if (
      ["amount", "total", "tip", "actual_fee", "refund_available"].includes(
        keyLower
      )
    )
      width = 18;
    else if (["status", "type", "card"].includes(keyLower)) width = 12;
    else if (keyLower.includes("donor")) width = 25;
    else if (keyLower.includes("created")) width = 12;

    return {
      header: String(filteredHeaders[idx] || key),
      key,
      width: width,
    };
  });

  // Add rows from sorted data with money field conversion
  sortedData.forEach((row) => {
    const cleanedRow = {};
    filteredKeys.forEach((k) => {
      if (moneyFields.includes(k) && row[k] != null) {
        // Convert cents to dollars for Excel/CSV too
        cleanedRow[k] = row[k] / 100;
      } else {
        cleanedRow[k] = row[k] == null ? "" : row[k];
      }
    });
    worksheet.addRow(cleanedRow);
  });

  const filterSuffix = objectToString(
    fiterFilters(filtersValues, ["page", "itemsPerPage"]),
    "="
  );

  // Add sort info to filename if applicable
  const sortSuffix =
    sortRules.length > 0
      ? `_sorted_${sortRules
          .map((rule) => `${rule.id}${rule.desc ? "_desc" : "_asc"}`)
          .join("_")}`
      : "";

  const fileName = `${exportFileName}_${filterSuffix}${sortSuffix}_${new Date()
    .toISOString()
    .slice(0, 10)}`;

  /* ---------------- CSV ---------------- */
  if (format === "csv") {
    // Format money fields for CSV
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber === 1) return; // Skip header row

      filteredKeys.forEach((key, colIndex) => {
        const cell = row.getCell(colIndex + 1);
        if (moneyFields.includes(key) && cell.value != null) {
          cell.value = `$${cell.value.toFixed(2)}`;
        }
      });
    });

    const csv = await workbook.csv.writeBuffer();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, `${fileName}.csv`);
    onSuccess?.();
  }

  /* ---------------- EXCEL ---------------- */
  if (format === "excel") {
    // Format money fields for Excel
    filteredKeys.forEach((key, colIndex) => {
      if (moneyFields.includes(key)) {
        const column = worksheet.getColumn(colIndex + 1);
        column.numFmt = '"$"#,##0.00';
      }
    });

    // Auto-fit columns
    worksheet.columns.forEach((column) => {
      let maxLength = 0;
      column.eachCell({ includeEmpty: true }, (cell) => {
        const columnLength = cell.value ? cell.value.toString().length : 10;
        if (columnLength > maxLength) {
          maxLength = columnLength;
        }
      });
      column.width = Math.min(maxLength < 10 ? 10 : maxLength, 50);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${fileName}.xlsx`);
    onSuccess?.();
  }

  /* ---------------- PDF ---------------- */
  if (format === "pdf") {
    await performPDFExport({
      data: sortedData,
      columns,
      exportFileName,
      filtersValues,
      discardedFields,
      sortRules,
      shouldSort: false, // Already sorted above
      companyInfo,
      reportTitle,
      moneyFields,
      onSuccess,
    });
  }
}

// Export the functions
export {
  sortDataByRules,
  performFileExport,
  performPDFExport,
  DEFAULT_MONEY_FIELDS,
  loadAndEmbedLogo,
};
